import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsAPI } from '../../services/api';
import { notificationKeys } from '../../query/queryKeys';
import toast from '../../services/toast';
import {
  countUnreadInInbox,
  notificationTypesForInbox,
  NOTIFICATION_INBOXES,
  type NotificationInboxId,
} from './notificationInboxUtils';

export const NOTIFICATIONS_PAGE_SIZE = 20;

type ReadFilter = 'all' | 'unread';

export function useNotificationsQueries({
  inboxId,
  readFilter,
  page,
  pageSize = NOTIFICATIONS_PAGE_SIZE,
}: {
  inboxId: NotificationInboxId;
  readFilter: ReadFilter;
  page: number;
  pageSize?: number;
}) {
  const queryClient = useQueryClient();
  const types = notificationTypesForInbox(inboxId);
  const offset = page * pageSize;

  const listQuery = useQuery({
    queryKey: notificationKeys.list({ inboxId, readFilter, page, pageSize }),
    queryFn: () =>
      notificationsAPI.getAll({
        limit: pageSize,
        offset,
        unreadOnly: readFilter === 'unread',
        types,
      }),
    staleTime: 30_000,
  });

  const unreadQuery = useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => notificationsAPI.getUnreadCount(),
    staleTime: 30_000,
  });

  const unreadBadgeQuery = useQuery({
    queryKey: notificationKeys.unreadBadgeList(),
    queryFn: () => notificationsAPI.getAll({ limit: 100, offset: 0, unreadOnly: true }),
    staleTime: 30_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsAPI.markRead(id),
    onSuccess: () => invalidate(),
    onError: (err: unknown) =>
      toast.error((err as Error).message || 'Failed to mark notification as read'),
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsAPI.markAllRead(),
    onSuccess: () => invalidate(),
    onError: (err: unknown) => toast.error((err as Error).message || 'Failed to mark all as read'),
  });

  const notifications = listQuery.data?.notifications ?? [];
  const total = listQuery.data?.total ?? 0;
  const unreadCount = unreadQuery.data?.count ?? 0;
  const loading = listQuery.isLoading;

  const inboxUnreadCounts = Object.fromEntries(
    NOTIFICATION_INBOXES.map((inbox) => [
      inbox.id,
      countUnreadInInbox(unreadBadgeQuery.data?.notifications ?? [], inbox.id),
    ])
  ) as Record<NotificationInboxId, number>;

  return {
    notifications,
    total,
    unreadCount,
    inboxUnreadCounts,
    loading,
    markReadMutation,
    markAllReadMutation,
    refetch: listQuery.refetch,
  };
}
